import React, { useRef, useState, memo, useEffect, useCallback } from 'react'
import { Alert, Button, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { MdLibraryAdd } from 'react-icons/md'
import { emptyGuid } from '../../../utils/emptyGuid.js'
import PositionItem from './PositionItem.jsx'
import ArticleSelectModal from '../../ArticleSelect/ArticleSelectModal.jsx'
import styled from 'styled-components'
import { useMemo } from 'react'

const BulkActionBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin: 0 0 16px 0;
  flex-wrap: wrap;
  justify-content: flex-end;

  .bulk-select {
    min-width: 320px;
  }

  @media (max-width: 576px) {
    flex-direction: column;

    .applyButton {
      width: 100%;
    }

    .bulk-select {
      width: 100%;
      min-width: unset;
    }
  }
`

const AddPosition = ({ handleAddArticle }) => {
  const { t } = useTranslation()
  const [showAddArticlesModal, setShowAddArticlesModal] = useState(false)
  const [selectedArticles, setSelectedArticles] = useState([])
  const [selectedArticleId, setSelectedArticleId] = useState(emptyGuid)

  useEffect(() => {
    if (!selectedArticles.length) {
      setSelectedArticleId(emptyGuid)
    } else {
      setSelectedArticleId(selectedArticles[0].article.id)
    }
  }, [setSelectedArticleId, selectedArticles])
  return (
    <>
      <Button onClick={() => setShowAddArticlesModal(true)} disabled={false}>
        <MdLibraryAdd className='me-3' />
        {t('returnsProcessingPage:addPosition')}
      </Button>
      {showAddArticlesModal ? (
        <ArticleSelectModal
          selectedArticles={selectedArticles}
          setSelectedArticles={setSelectedArticles}
          setShowAddArticlesModal={setShowAddArticlesModal}
          showAddArticlesModal={showAddArticlesModal}
          handleSave={() => {
            setShowAddArticlesModal(false)
            handleAddArticle(selectedArticleId)
            setSelectedArticles([])
          }}
          singleSelection={true}
          userOffices={true}
        />
      ) : null}
    </>
  )
}

const PositionList = memo(
  ({
    positions,
    addPosition,
    copyPosition,
    splitPosition,
    setPositions,
    actionMode,
    formErrors,
    resetErrorByFormKey,
  }) => {
    const { t } = useTranslation()
    const [bulkActionToPerform, setBulkActionToPerform] = useState('nONE')
    const [appliedInfo, setAppliedInfo] = useState('')

    const isStoring = () => {
      return actionMode === 'store'
    }
    const [nextFocus, setNextFocus] = useState(null)
    const ref = useRef()
    const handleNextFocus = (id, name) => {
      let nextFocusParam = {
        id: id,
        name: name,
      }
      setNextFocus(nextFocusParam)
    }
    const focusInput = useCallback(() => {
      let id = nextFocus.id
      let name = nextFocus.name
      const allTagInputs = document.getElementsByTagName('input') //=>allInputs is a HTMLCollection not an Array!!!
      let allNameInputs = []
      const checkKeysExists = (elements, id) => {
        for (let i = 0; i < elements.length; i++) {
          if (!elements[i].disabled && elements[i].id === id.replace('-qc_', '-sk_')) {
            return true
          }
          if (!elements[i].disabled && elements[i].id === id.replace('-qc_', '-rfid_')) {
            return true
          }
          if (!elements[i].disabled && elements[i].id === id.replace('-qc_', '-bk_')) {
            return true
          }
        }
        return false
      }
      for (let i = 0; i < allTagInputs.length; i++) {
        if (!allTagInputs[i].disabled) {
          /*if (allTagInputs[i].id.includes(`${name}_`)) {
            allNameInputs.push(allTagInputs[i])
          }*/
          if (allTagInputs[i].id.includes(`sk_`)) {
            allNameInputs.push(allTagInputs[i])
          }
          if (allTagInputs[i].id.includes(`rfid_`)) {
            allNameInputs.push(allTagInputs[i])
          }
          if (allTagInputs[i].id.includes(`bk_`)) {
            allNameInputs.push(allTagInputs[i])
          }
          if (allTagInputs[i].id.includes(`qc_`)) {
            if (name === 'quantityCounted' || !checkKeysExists(allTagInputs, allTagInputs[i].id)) {
              allNameInputs.push(allTagInputs[i])
            }
          }
        }
      }
      let foundIndex = allNameInputs.findIndex((item) => item.id === id)
      let nextfield =
        foundIndex >= 0 && allNameInputs[foundIndex + 1]
          ? allNameInputs[foundIndex + 1]
          : allNameInputs[0]
      // If found, focus the next field
      if (nextfield) {
        nextfield.focus()
      }
      setNextFocus(null)
    }, [nextFocus])

    const handleDeletePosition = (id) => {
      let index = positions.findIndex((item) => item.id === id)
      if (index >= 0) {
        const newPosistions = [...positions]
        newPosistions.splice(index, 1)
        setPositions(newPosistions)
      }
    }
    const handleChangePositionByKey = (id, key, value) => {
      let index = positions.findIndex((item) => item.id === id)
      if (index >= 0) {
        const newPosistions = [...positions]
        switch (key) {
          case 'articleVariant':
            newPosistions[index].articleVariant = { id: value }
            break
          case 'enforceUpdate':
            delete newPosistions[index][key]
            break
          default:
            newPosistions[index][key] = value
            break
        }
        setPositions(newPosistions)
      }
    }
    const bulkActionOptions = useMemo(
      () => [
        { value: 'nONE', label: t('pleaseSelect') },
        { value: 'store', label: t('returnsProcessingPage:storeAction') },
        { value: 'dispose', label: t('returnsProcessingPage:disposeAction') },
        { value: 'markLost', label: t('returnsProcessingPage:markLostAction') },
      ],
      [t]
    )
    const [onlyApplyToEmpty] = useState(false)

    const isEmptyAction = (value) =>
      !value || value === 'nONE' || value === 'none' || value === 'NONE'

    const applyActionToPerformForAll = () => {
      if (!bulkActionToPerform || bulkActionToPerform === 'nONE') return

      let appliedCount = 0

      const newPositions = positions.map((pos) => {
        const hasRealAction = !isEmptyAction(pos.actionToPerform)

        if (onlyApplyToEmpty && hasRealAction) return pos

        if (pos.actionToPerform !== bulkActionToPerform) appliedCount += 1

        return {
          ...pos,
          actionToPerform: bulkActionToPerform,
        }
      })

      setPositions(newPositions)
      setBulkActionToPerform('nONE')

      setAppliedInfo(
        appliedCount
          ? t('goodsReceiptPage:bulkApplySuccess', { count: appliedCount })
          : t('goodsReceiptPage:bulkApplyNothing')
      )

      setTimeout(() => setAppliedInfo(''), 2500)
    }

    const showBulkActions = isStoring() && positions.length > 0
    const renderPosition = (position, index) => {
      return (
        <PositionItem
          key={position.id}
          position={position}
          deletable={!isStoring()}
          handleCopyPosition={copyPosition}
          handleSplitPosition={splitPosition}
          handleDeletePosition={handleDeletePosition}
          handleChangePositionByKey={handleChangePositionByKey}
          isStoring={isStoring()}
          index={index}
          formErrors={formErrors}
          resetErrorByFormKey={resetErrorByFormKey}
          handleNextFocus={handleNextFocus}
        />
      )
    }
    useEffect(() => {
      if (nextFocus) {
        const timer = setTimeout(() => {
          focusInput()
        }, 20)
        return () => clearTimeout(timer)
      }
    }, [nextFocus, focusInput])
    return (
      <div ref={ref}>
        <h1>{t('returnsReceiptOverlayProgress:positions')}</h1>
        {showBulkActions ? (
          <BulkActionBar>
            <div className='bulk-select'>
              <Form.Label className='mb-1'>
                {t('goodsReceiptPage:bulkActions')}
              </Form.Label>
              <Form.Select
                value={bulkActionToPerform}
                onChange={(e) => setBulkActionToPerform(e.target.value)}
              >
                {bulkActionOptions.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            <Button
              className='applyButton'
              variant='primary'
              disabled={!bulkActionToPerform || bulkActionToPerform === 'nONE'}
              onClick={applyActionToPerformForAll}
            >
              {t('goodsReceiptPage:applyBulkAction')}
            </Button>
          </BulkActionBar>
        ) : null}
        {appliedInfo ? (
          <Alert variant='info' className='py-2'>
            {appliedInfo}
          </Alert>
        ) : null}

        <div className='spacing-down'>
          {positions.length === 0 && (
            <Alert variant='info'>
              {t('returnsProcessingPage:noPositions')}
            </Alert>
          )}
          {!!formErrors['positions'] && (
            <Alert variant='danger'>{formErrors['positions']}</Alert>
          )}
          {positions.map((item, index) => renderPosition(item, index))}
          {!isStoring() && (
            <>
              <div className='mb-1' />
              <AddPosition handleAddArticle={addPosition} />
            </>
          )}
        </div>
      </div>
    )
  }
)

export default PositionList
